// /src/Components/Clients/AdminCampaignLog.jsx
import React, { useContext, useEffect, useMemo, useState, Fragment } from "react";
import { Toaster, toast } from "react-hot-toast";
import { Dialog, Transition } from "@headlessui/react";
import { GlobalContext } from "../../Context/GlobalContext";
import { Building2, Filter, RefreshCcw, Eye, Download } from "lucide-react";
import * as XLSX from "xlsx";

import useWhatsapp from "../Services/WhatsappServices/useWhatsapp";
const PAGE_SIZE = 200;

// ---- Small UI helpers ----
function Badge({ children, onClear }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border bg-gray-50 px-3 py-1 text-xs">
      {children}
      <button className="rounded-full px-1.5 text-gray-500 hover:bg-gray-200" onClick={onClear}>✕</button>
    </span>
  );
}
function Pill({ color = "emerald", children }) {
  const map = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    sky: "border-sky-200 bg-sky-50 text-sky-700",
    zinc: "border-zinc-200 bg-zinc-50 text-zinc-700",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${map[color]}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}
function statusToPill(status) {
  const s = String(status || "").toLowerCase();
  if (["completed", "delivered", "success", "read", "accepted"].includes(s)) return { color: "emerald", label: status };
  if (["failed"].includes(s)) return { color: "rose", label: status };
  if (["running"].includes(s)) return { color: "amber", label: status };
  if (["stopped", "paused"].includes(s)) return { color: "zinc", label: status };
  return { color: "sky", label: status || "—" };
}

export default function AdminCampaignLog({ Admin }) {
  const {getAllCampaignsSuperAdmin, getWhatsappLogs, getWhatsappTemplatesV1}=useWhatsapp();
   
  const { api, adminId } = useContext(GlobalContext); // ✅ use adminId from context
  const parsedAdmin = typeof Admin === "string" ? JSON.parse(Admin) : Admin;

  // Filters
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Data
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  // Logs modal (fixed-size, scrollable content)
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsBusy, setLogsBusy] = useState(false);
  const [logsTitle, setLogsTitle] = useState("");
  const [logs, setLogs] = useState([]);

  // Template preview modal (kept)
  const [templates, setTemplates] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Initial date defaults
  useEffect(() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    const todayStr = `${y}-${m}-${d}`;

    const back = new Date();
    back.setDate(back.getDate() - 3);
    const by = back.getFullYear();
    const bm = String(back.getMonth() + 1).padStart(2, "0");
    const bd = String(back.getDate()).padStart(2, "0");
    const backStr = `${by}-${bm}-${bd}`;

    setStartDate(backStr);
    setEndDate(todayStr);
  }, []);

  // Fetch campaigns (auth: adminId from context)
 const fetchCampaigns = async () => {
  setLoading(true);

  try {
    const result =
      await getAllCampaignsSuperAdmin(
        adminId
      );

    if (result.success) {
      setCampaigns(result.data);
      setPage(1);
    } else {
      toast.error(result.message);
    }
  } catch (e) {
    toast.error("Error loading campaigns");
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminId]);

  // Filtered + paged rows
  const sorted = useMemo(
    () => [...(campaigns || [])].sort((a, b) => new Date(b.indate || 0) - new Date(a.indate || 0)),
    [campaigns]
  );

  const filtered = useMemo(() => {
    const s = (v) => String(v ?? "").toLowerCase();
    return sorted.filter((r) => {
      const matchesQ =
        !q ||
        s(r.id).includes(q.toLowerCase()) ||
        s(r.user_name).includes(q.toLowerCase()) ||
        s(r.campaign_name).includes(q.toLowerCase()) ||
        s(r.template_category).includes(q.toLowerCase()) ||
        s(r.status).includes(q.toLowerCase());
      const matchesStatus = !status || s(r.status) === status.toLowerCase();
      const matchesCategory = !category || s(r.template_category) === category.toLowerCase();
      // keep date fields for later server-side filtering (UI only now)
      return matchesQ && matchesStatus && matchesCategory;
    });
  }, [sorted, q, status, category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = (p) => Math.min(totalPages, Math.max(1, p));
  const pageRows = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const clearFilters = () => {
    setQ("");
    setStatus("");
    setCategory("");
  };

  // Logs modal controls
 const openLogs = async ({
  status,
  id,
  name,
  userid,
}) => {
  setLogsTitle(
    `${name} — ${String(status || "")
      .toUpperCase()}`
  );

  setLogsOpen(true);
  setLogsBusy(true);
  setLogs([]);

  try {
    const result =
      await getWhatsappLogs({
        userid,
        campaignId: id,
        status,
      });

    if (result.success) {
      setLogs(result.data);
    } else {
      toast.error(result.message);
    }
  } catch (e) {
    toast.error("Error loading logs");
  } finally {
    setLogsBusy(false);
  }
};

  // Template preview utilities
  const extractTemplateTexts = (template) => {
    let headerText = "", bodyText = "", footerText = "";
    if (template?.components?.length) {
      for (const c of template.components) {
        if (c.type === "HEADER" && c.text) headerText = c.text;
        if (c.type === "BODY" && c.text) bodyText = c.text;
        if (c.type === "FOOTER" && c.text) footerText = c.text;
      }
    }
    return { headerText, bodyText, footerText };
  };
  const fillBodyVariables = (text) => {
    const defaults = ["John Doe", "Messaging"];
    return (text || "").replace(/{{(\d+)}}/g, (_, i) => defaults[i - 1] || "");
  };

  const loadTemplates = async (
  userid,
  phoneNumberId
) => {
  try {
    const result =
      await getWhatsappTemplatesV1({
        userid,
        phoneNumberId,
      });

    if (result.success) {
      setTemplates(result.data);
    } else {
      toast.error(result.message);
    }
  } catch (e) {
    toast.error("Error loading templates");
  }
};

  const openPreview = async (templateId, userid, phoneNumberId) => {
    await loadTemplates(userid, phoneNumberId);
    const t = (templates || []).find((x) => x.name === templateId);
    setPreviewTemplate(t || null);
    setPreviewOpen(true);
  };

  const exportLogsExcel = () => {
    if (!logs?.length) return;
    const headers = [
      ["ID","Contact","Business Acc. Id","Phone No. Id","Status","Date","Product","Category","Contact Name","Message Category","Error Code","Error Description"],
    ];
    const rows = logs.map((x) => [
      x.id, x.contact, x.business_acc_id, x.phone_no_id, x.status, x.indate, x.product, x.category,
      x.contact_name || "N/A", x.message_category, x.error_code || "N/A", x.error || "N/A",
    ]);
    const sheet = XLSX.utils.aoa_to_sheet([...headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "Campaign Logs");
    XLSX.writeFile(wb, `Whatsapp_Logs_${logsTitle.replace(/\s+/g, "_")}.xlsx`);
  };

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />

      {/* Page header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <Building2 size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">WhatsApp Campaign Logs</h1>
            <p className="text-sm text-gray-500">Search, preview templates, drill into delivery logs</p>
          </div>
        </div>
        <button
          className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
          onClick={fetchCampaigns}
        >
          <RefreshCcw size={16} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border bg-white p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Filter size={16} /> Filters
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Search</div>
            <input
              className="h-10 w-80 rounded-xl border px-3 text-sm outline-none"
              placeholder="ID / user / campaign / category / status"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
            />
          </label>

          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Status</div>
            <select
              className="h-10 w-44 rounded-xl border bg-white px-3 text-sm outline-none"
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            >
              <option value="">All</option>
              <option>running</option>
              <option>completed</option>
              <option>stopped</option>
              <option>failed</option>
              <option>delivered</option>
              <option>read</option>
              <option>accepted</option>
              <option>responded</option>
              <option>sent</option>
            </select>
          </label>

          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Template Category</div>
            <input
              className="h-10 w-56 rounded-xl border px-3 text-sm outline-none"
              placeholder="e.g., MARKETING / UTILITY"
              value={category}
              onChange={(e) => { setCategory(e.target.value); setPage(1); }}
            />
          </label>

          {/* Optional date inputs (for future server-side filtering) */}
          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Start</div>
            <input
              type="date"
              className="h-10 w-44 rounded-xl border px-3 text-sm outline-none"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">End</div>
            <input
              type="date"
              className="h-10 w-44 rounded-xl border px-3 text-sm outline-none"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>

          <div className="ml-auto flex gap-2">
            <button
              className="inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
              onClick={clearFilters}
              disabled={loading}
            >
              Clear
            </button>
            <button
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90"
              onClick={fetchCampaigns}
              disabled={loading}
            >
              {loading ? "Applying…" : "Apply"}
            </button>
          </div>
        </div>

        {(q || status || category) && (
          <div className="mt-2 flex flex-wrap gap-2">
            {q && <Badge onClear={() => setQ("")}>Search: {q}</Badge>}
            {status && <Badge onClear={() => setStatus("")}>Status: {status}</Badge>}
            {category && <Badge onClear={() => setCategory("")}>Category: {category}</Badge>}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="mt-3 overflow-hidden rounded-2xl border bg-white">
        <div className="overflow-auto">
          <table className="min-w-full table-fixed text-left text-sm">
            {/* ✅ Force consistent column widths to prevent misalignment */}
            <colgroup>
              <col className="w-14" />
              <col className="w-24" />
              <col className="w-32" />
              <col className="w-40" />
              <col className="w-36" />
              <col className="w-36" />
              <col className="w-44" />
              <col className="w-32" />
              <col className="w-24" />
              <col className="w-28" />
              <col className="w-28" />
              <col className="w-24" />
              <col className="w-24" />
              <col className="w-28" />
              <col className="w-28" />
              <col className="w-28" />
              <col className="w-28" />
            </colgroup>
            <thead className="bg-[#01C37D] text-white">
              <tr className="whitespace-nowrap">
                {[
                  "S/N","ID","User","Campaign","Start","End","Template","Category",
                  "Sent","Delivered","Accepted","Read","Failed","Responded","Preview","Audience","Status",
                ].map((h) => (
                  <th key={h} className="px-4 py-2 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && pageRows.length === 0 && (
                <tr>
                  <td colSpan={17} className="px-4 py-8 text-center text-gray-500">
                    No records found.
                  </td>
                </tr>
              )}

              {pageRows.map((item, idx) => {
                const pill = statusToPill(item.status);
                return (
                  <tr key={`${item.id}-${idx}`} className="border-t align-top">
                    <td className="px-4 py-2">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                    <td className="px-4 py-2">{item.id}</td>
                    <td className="px-4 py-2">{item.user_name}</td>
                    <td className="px-4 py-2">{String(item.campaign_name || "").toUpperCase()}</td>
                    <td className="px-4 py-2">{item.campaign_start || "—"}</td>
                    <td className="px-4 py-2">{item.campaign_end || "—"}</td>
                    <td className="px-4 py-2" title={item.template_id}>{item.template_id || "—"}</td>
                    <td className="px-4 py-2"><b>{item.template_category || "—"}</b></td>

                    {/* Counters -> open fixed-size modal with scrollable content */}
                    <td className="px-4 py-2">
                      <button className="text-[#01C37D] hover:underline"
                        onClick={() => openLogs({ status: "sent", id: item.id, name: item.campaign_name, userid: item.userid })}
                      >
                        <b>{item.sent || 0}</b>
                      </button>
                    </td>
                    <td className="px-4 py-2">
                      <button className="text-[#01C37D] hover:underline"
                        onClick={() => openLogs({ status: "delivered", id: item.id, name: item.campaign_name, userid: item.userid })}
                      >
                        <b>{item.delivered || 0}</b>
                      </button>
                    </td>
                    <td className="px-4 py-2">
                      <button className="text-[#01C37D] hover:underline"
                        onClick={() => openLogs({ status: "accepted", id: item.id, name: item.campaign_name, userid: item.userid })}
                      >
                        <b>{item.accepted || 0}</b>
                      </button>
                    </td>
                    <td className="px-4 py-2">
                      <button className="text-[#01C37D] hover:underline"
                        onClick={() => openLogs({ status: "read", id: item.id, name: item.campaign_name, userid: item.userid })}
                      >
                        <b>{item.read || 0}</b>
                      </button>
                    </td>
                    <td className="px-4 py-2">
                      <button className="text-[#01C37D] hover:underline"
                        onClick={() => openLogs({ status: "failed", id: item.id, name: item.campaign_name, userid: item.userid })}
                      >
                        <b>{item.failed || 0}</b>
                      </button>
                    </td>
                    <td className="px-4 py-2">
                      <button className="text-[#01C37D] hover:underline"
                        onClick={() => openLogs({ status: "responded", id: item.id, name: item.campaign_name, userid: item.userid })}
                      >
                        <b>{item.responded || 0}</b>
                      </button>
                    </td>

                    {/* Preview */}
                    <td className="px-4 py-2">
                      <button
                        className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1 text-xs hover:bg-gray-50"
                        onClick={() => openPreview(item.template_id, item.userid, item.phone_no_id)}
                        title="Preview template"
                      >
                        <Eye size={14} /> Preview
                      </button>
                    </td>

                    {/* Audience excel */}
                    <td className="px-4 py-2">
                      {item.excel_url ? (
                        <a
                          className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1 text-xs hover:bg-gray-50"
                          href={item.excel_url}
                          target="_blank"
                          rel="noreferrer"
                          title="Download audience"
                        >
                          <Download size={14} /> Download
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>

                    {/* Status pill */}
                    <td className="px-4 py-2">
                      <Pill color={pill.color}>{pill.label}</Pill>
                    </td>
                  </tr>
                );
              })}
              {loading && (
                <tr>
                  <td colSpan={17} className="px-4 py-6 text-center text-gray-500">Loading…</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-2 p-3">
            <button
              className="h-9 rounded-xl border px-3 text-sm"
              onClick={() => setPage((p) => pageSafe(p - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <span className="text-sm text-gray-600">
              Page <b>{page}</b> of <b>{totalPages}</b>
            </span>
            <button
              className="h-9 rounded-xl border px-3 text-sm"
              onClick={() => setPage((p) => pageSafe(p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* ---- Fixed-size Modal: Logs table (scrolls inside) ---- */}
      <Transition show={logsOpen} as={Fragment}>
        <Dialog onClose={() => { if (!logsBusy) setLogsOpen(false); }} className="relative z-50">
          <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 grid place-items-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              {/* ✅ Fixed max width, content scrolls inside */}
              <Dialog.Panel className="w-full max-w-5xl rounded-2xl bg-white p-4 shadow-xl">
                <div className="mb-3 flex items-center justify-between">
                  <Dialog.Title className="text-base font-semibold">{logsTitle}</Dialog.Title>
                  <div className="flex items-center gap-2">
                    {!!logs.length && (
                      <button
                        className="inline-flex items-center gap-1 rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
                        onClick={exportLogsExcel}
                      >
                        <Download size={14} /> Export
                      </button>
                    )}
                    <button className="h-8 rounded-xl border px-3 text-sm" onClick={() => setLogsOpen(false)} disabled={logsBusy}>Close</button>
                  </div>
                </div>

                {/* Scroll container */}
                <div className="max-h-[70vh] overflow-auto rounded-xl border">
                  <table className="min-w-full table-fixed text-left text-sm">
                    <colgroup>
                      <col className="w-20" />
                      <col className="w-40" />
                      <col className="w-40" />
                      <col className="w-40" />
                      <col className="w-28" />
                      <col className="w-40" />
                      <col className="w-32" />
                      <col className="w-28" />
                      <col className="w-36" />
                      <col className="w-40" />
                      <col className="w-28" />
                      <col className="w-48" />
                    </colgroup>
                    <thead className="bg-[#01C37D] text-white">
                      <tr className="whitespace-nowrap">
                        {["ID","Contact","Business Acc. Id","Phone No. Id","Status","Date","Product","Category","Contact Name","Message Category","Error Code","Error Description"].map(h => (
                          <th key={h} className="px-4 py-2 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {logsBusy && (
                        <tr><td colSpan={12} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>
                      )}
                      {!logsBusy && logs.length === 0 && (
                        <tr><td colSpan={12} className="px-4 py-8 text-center text-gray-500">No log records.</td></tr>
                      )}
                      {!logsBusy && logs.map((x) => (
                        <tr key={x.id} className="border-t align-top">
                          <td className="px-4 py-2">{x.id}</td>
                          <td className="px-4 py-2">{x.contact}</td>
                          <td className="px-4 py-2">{x.business_acc_id}</td>
                          <td className="px-4 py-2">{x.phone_no_id}</td>
                          <td className="px-4 py-2">{x.status}</td>
                          <td className="px-4 py-2">{x.indate}</td>
                          <td className="px-4 py-2">{x.product}</td>
                          <td className="px-4 py-2">{x.category}</td>
                          <td className="px-4 py-2">{x.contact_name || "N/A"}</td>
                          <td className="px-4 py-2">{x.message_category}</td>
                          <td className="px-4 py-2">{x.error_code || "N/A"}</td>
                          <td className="px-4 py-2">{x.error || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* ---- Modal: Template preview (unchanged layout) ---- */}
      <Transition show={previewOpen} as={Fragment}>
        <Dialog onClose={() => setPreviewOpen(false)} className="relative z-50">
          <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 grid place-items-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
                <Dialog.Title className="text-base font-semibold">Message Preview</Dialog.Title>
                <div className="mt-3 rounded-xl border p-3">
                  {!previewTemplate ? (
                    <div className="py-10 text-center text-sm text-gray-500">No template found.</div>
                  ) : (
                    <TemplatePreview template={previewTemplate} fillBodyVariables={fillBodyVariables} extractTemplateTexts={extractTemplateTexts} />
                  )}
                </div>
                <div className="mt-4 flex justify-end">
                  <button className="h-9 rounded-xl border px-3 text-sm" onClick={() => setPreviewOpen(false)}>Close</button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

function TemplatePreview({ template, fillBodyVariables, extractTemplateTexts }) {
  const { headerText, bodyText, footerText } = extractTemplateTexts(template);
  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-500">
        {template.components?.find((c) => c.format === "IMAGE") && "Header contains IMAGE"}
        {template.components?.find((c) => c.format === "VIDEO") && " Header contains VIDEO"}
        {template.components?.find((c) => c.format === "DOCUMENT") && " Header contains DOCUMENT"}
      </div>
      {headerText && <div className="text-sm font-semibold">{headerText}</div>}
      {bodyText && <div className="whitespace-pre-wrap text-sm">{fillBodyVariables(bodyText)}</div>}
      {footerText && <div className="text-xs text-gray-500">{footerText}</div>}
      <div className="pt-2">
        {(template.components?.find((c) => c.type === "BUTTONS")?.buttons || []).map((b, i) => (
          <div key={i} className="mb-2 rounded-xl border px-3 py-1 text-center text-sm">
            {b.text}
          </div>
        ))}
      </div>
    </div>
  );
}
